/**
 * Every user-facing string the relations step shows, plus the pure text transforms that turn
 * the graph's wire words into label text.
 *
 * The copy lives in one frozen object for the same reason the workspace copy does
 * (`config/compositeSourceEnums.js`): the wizard waves reuse the wording, and it changes in one
 * place. Nothing here names a register, a tag or any other entity — the relations step describes
 * whatever the authoring graph offered (DS-11), so its wording has to survive task boards
 * becoming sources without an edit.
 *
 * The transforms below are TRANSFORMS, not a catalogue: `humanizeWireWord` reshapes any camelCase
 * word the server sends, so a kind this file has never heard of still renders as readable text.
 */

import { CompositeStrategy, NodeKindLabelOverrides } from '../../model/definitionVocabulary';

/** Which of the two views of the same definition is showing. */
export const RelationsView = Object.freeze({
  Tree: 'tree',
  Diagram: 'diagram',
});

/** What the edge popover is asking for: which traversal, or how to fold one in. */
export const EdgePopoverMode = Object.freeze({
  Menu: 'menu',
  Configure: 'configure',
});

export const RelationsCopy = Object.freeze({
  StepIntro:
    'Add the linked data this source should carry. Every option is discovered from the database\'s own relations.',
  ViewsLabel: 'Relations view',
  TreeTab: 'Tree',
  DiagramTab: 'Diagram',
  AddRelation: 'Add relation',
  RemoveRelation: 'Remove relation',
  ConfigureRelation: 'Change how this relation folds in',
  RootBadge: 'Root',
  NoRootTitle: 'Nothing to relate yet',
  NoRootMessage: 'Choose the data this source is built from, then come back to join the records around it.',
  GraphLoadingNote: 'Loading the relations available from this starting data…',
  NoGraphNote: 'The relations for this starting data could not be loaded. Return to Source, then try again.',
  EdgeLimitNote: 'This source already holds every relation the engine allows.',
  NoTraversalsNote: 'Nothing links to this one — it is the end of the line.',
  MenuHeading: 'Related data — introspected',
  DiscriminatorHeading: 'This relation lands on the tag table — which tag type?',
  StrategyHeading: 'Several records can match one row — which one belongs on the row?',
  StrategyHeadingToOne: 'How should this record fold into the row?',
  OrderByLabel: 'Newest by',
  OrderByHint: 'The column that decides which match is the latest.',
  DateColumnsGroup: 'Date columns',
  OtherColumnsGroup: 'Other columns',
  AliasLabel: 'Column name',
  AliasHint: 'This relation adds one column — name it. Spaces are fine; they become underscores.',
  AliasDerivedPrefix: 'Saved as',
  ListPropertyLabel: 'Property to aggregate',
  ListPropertyHint: 'The column whose values are joined into the list.',
  Cancel: 'Cancel',
  Confirm: 'Add relation',
  Save: 'Save relation',
  NeedsDiscriminator: 'Pick the tag type first.',
  RegisterTargetLabel: 'Register to link through shared tags',
  RegisterTargetHint: 'Only materialized Registers in this facility are available.',
  NoRegisterTargets: 'No materialized Registers are available in this facility.',
  NeedsRegisterTarget: 'Pick the Register whose rows should be linked.',
  NeedsStrategy: 'Pick how this relation folds in.',
  NeedsOrderBy: 'Pick the column that decides which match is the latest.',
  NeedsAlias: 'Name the column this relation adds.',
  NoColumnsToRankBy: 'This target exposes no columns to rank by.',
  FanOutBannerTitle: 'This source fans out',
  FanOutBannerBody: 'One row per deepest child match. Parent values repeat for every child row, so counts and totals include those repeats.',
  ScalarNodeNote: 'Adds one column',
  ToManyBadge: 'To-many',
  ToOneBadge: 'To-one',
  DiagramHint: 'Click a node to focus its connections — add with the green +, drag empty space to pan.',
  ZoomIn: 'Zoom in',
  ZoomOut: 'Zoom out',
  ZoomFit: 'Fit',
  LegendPlain: 'One value per row',
  LegendFanOut: 'Fan-out (multiplies rows)',
});

/**
 * How each strategy is offered in the popover. Keyed by the WIRE word so the radio list is built
 * from the graph's own `strategies` array rather than from a fixed list of radios.
 *
 * `worst` is deliberately absent: it is on the plan's do-not-port list and the server's vocabulary
 * does not carry it either, so there is nothing to describe.
 */
export const StrategyCopy = Object.freeze({
  [CompositeStrategy.Single]: Object.freeze({
    title: 'The matching record',
    description: 'At most one match — its columns join straight onto the row.',
    chip: 'Single',
  }),
  [CompositeStrategy.Latest]: Object.freeze({
    title: 'Latest one',
    description: 'Newest by the column you pick.',
    chip: 'Latest',
  }),
  [CompositeStrategy.Count]: Object.freeze({
    title: 'Count',
    description: 'One number column — how many are linked.',
    chip: 'Count',
  }),
  [CompositeStrategy.Exists]: Object.freeze({
    title: 'Exists',
    description: 'One yes/no column.',
    chip: 'Exists',
  }),
  [CompositeStrategy.FanOut]: Object.freeze({
    title: 'All of them (fan-out)',
    description: 'Multiplies rows — one row per match instead of one per source row.',
    chip: 'Fan-out',
  }),
  [CompositeStrategy.List]: Object.freeze({
    title: 'List of values (comma-separated)',
    description: 'Combines all matching values into one text column without multiplying rows.',
    chip: 'List',
  }),
});

/**
 * A wire word as readable text: `masterTag` becomes `Master tag`, `register` becomes `Register`.
 *
 * Words that already start with a capital are returned untouched, because those are the server's
 * own proper names — a TPH discriminator like `ForgePipeline` is what the author sees elsewhere in
 * the product and rewriting it to `Forge pipeline` would make it a different word.
 */
export function humanizeWireWord(word) {
  const text = word === null || word === undefined ? '' : String(word);
  if (!text) return '';
  if (Object.prototype.hasOwnProperty.call(NodeKindLabelOverrides, text)) {
    return NodeKindLabelOverrides[text];
  }
  if (text.charAt(0) !== text.charAt(0).toLowerCase()) return text;

  const spaced = text.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The strategy's own words, or a humanized fallback for one this file has never heard of. */
export function strategyCopyOf(kind) {
  return StrategyCopy[kind] || {
    title: humanizeWireWord(kind),
    description: '',
    chip: humanizeWireWord(kind),
  };
}

/**
 * The chip a node card wears: the strategy, and for `latest` the column it ranks by, so the
 * decision that shapes the row is readable without opening anything.
 */
export function describeStrategyChip(strategy) {
  if (!strategy || !strategy.kind) return '';
  const chip = strategyCopyOf(strategy.kind).chip;
  if (strategy.kind === CompositeStrategy.Latest && strategy.orderBy) {
    return `${chip} by ${strategy.orderBy}`;
  }
  if (strategy.kind === CompositeStrategy.List && strategy.column) {
    return `${chip} of ${strategy.column}`;
  }
  return chip;
}

export default RelationsCopy;
